<template>
  <div class="d-flex flex-column">
    <Invitees hide-title layout-stable-expert />

    <button
      :aria-expanded="showAdditionalSettings"
      class="additional-settings-toggle"
      data-test-id="additional-settings-toggle"
      type="button"
      @click="toggleAdditionalSettings"
    >
      <v-icon class="additional-settings-toggle__icon" size="14">
        {{ showAdditionalSettings ? 'fa-caret-down' : 'fas fa-caret-right' }}
      </v-icon>
      <span class="additional-settings-toggle__label">{{
        i18n.t('titles.additional_settings')
      }}</span>
    </button>
    <!-- Shown/hidden outright, no expand transition: animating the height
           clips its contents mid-flight, which reads as the settings being cut
           off rather than revealed. The dialog body is a fixed-size scroll
           viewport, so an instant toggle shifts nothing around it. -->
    <div v-show="showAdditionalSettings" ref="additionalSettingsSection">
      <!-- Direct slot siblings so DeepDialog's cardActionsBlockOnMobile can
        stack them in a column on xs (same pattern as CreateDocumentDialog).
        me-auto keeps the toggle on the left on desktop. -->
      <ExpertSettingsToggle
        class="me-auto"
        data-test-id="show-additional-settings"
        :inert="isConfirming"
      />
      <AllowAttachmentsSetting />
      <GroupQuorumControlToggle />
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref } from 'vue';
import { useDisplay } from 'vuetify/framework';
import Invitees from '@/components/create/Invitees.vue';
import AllowAttachmentsSetting from '@/components/create/settings/AllowAttachmentsSetting.vue';
import GroupQuorumControlToggle from '@/components/create/settings/GroupQuorumControlToggle.vue';
import { useCreateStore } from '@/stores/create.ts';
import { useI18n } from 'vue-i18n';
import ExpertSettingsToggle from '@/components/create/settings/ExpertSettingsToggle.vue';
import { useSigningWorkflow } from '@/composables/create/useSigningWorkflow.ts';
import { useExpertSettings } from '@/composables/useExpertSettings.ts';
import { useQuorumControlSetting } from '@/composables/useQuorumControlSetting.ts';
const { expertSettings } = useExpertSettings();
const { quorumControl } = useQuorumControlSetting();
const createStore = useCreateStore();
const display = useDisplay();
const i18n = useI18n();
const { isConfirming, signeeAttachmentsAllowed } = useSigningWorkflow();

const additionalSettingsSection = ref<HTMLElement | null>(null);
// Collapsed by default; opened up-front when the setting it holds is already
// switched on, so an active enforcement is never hidden behind a closed panel.
const showAdditionalSettings = ref(false);

// The panel sits at the bottom of a fixed-height, scrollable body, so bring it
// into view once it is in the DOM.
async function toggleAdditionalSettings() {
  showAdditionalSettings.value = !showAdditionalSettings.value;
  if (!showAdditionalSettings.value) return;

  await nextTick();
  additionalSettingsSection.value?.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest'
  });
}

// The dialog renders this step with v-if, so it mounts every time the user
// lands on it — opening the dialog directly on Participants or navigating here
// from the mode step. Focus the first invitee still missing an email so typing
// can start right away. Desktop only, matching addSigneeField: on smaller
// screens focusing would pop the keyboard over the dialog. When no invitees
// exist yet, Invitees auto-adds one on mount and focuses it itself.
onMounted(() => {
  if (!display.lgAndUp.value) return;
  const firstEmpty = createStore.signeesOrderedFlat.find(
    (signee) => !signee.email
  );
  if (firstEmpty) void createStore.focusInputField(firstEmpty.signeeId);
  showAdditionalSettings.value =
    expertSettings.value ||
    signeeAttachmentsAllowed.value ||
    quorumControl.value;
});
</script>
<style scoped lang="scss">
// The whole row is the control, not just the label, so clicking anywhere along
// it toggles the panel.
.additional-settings-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 12px 0;
  background: none;
  border: 0;
  color: rgb(var(--v-theme-primary));
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  text-align: start;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: -2px;
  }
}

.additional-settings-toggle__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  flex-shrink: 0;
}

.additional-settings-toggle__label {
  display: inline-flex;
  align-items: center;
  line-height: 1;
}
</style>
